import { useState, useCallback, useRef, useEffect } from 'react';
import { postUGCVideoAssembly, pollUGCVideoStatus } from '../../../services/contentService';
import { MediaSegment } from '../types';
import { normalizeUGCVideoError } from '../utils/ugcVideoErrorMessages';
import { debug } from "../../../utils/debugLogger";

interface UseUGCVideoAssemblyProps {
  customerId: string | null;
  idToken: string;
  /** Called when post-assembly polling returns the final video URL; use to update preview. */
  onFinalUrl?: (url: string) => void;
}

interface AssemblyTextOverlay {
  text: string;
  time: number;
  duration: number;
  style?: string;
  position?: string;
  x_pct?: number;
  y_pct?: number;
  animation?: string;
  anim_duration?: number;
  animation_out?: string;
  anim_out_duration?: number;
  design?: string;
  font_size?: number;
  rotation?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  outline?: boolean;
  /** Outline (stroke) color when outline is on. Falls back to a contrast default. */
  outline_color?: string;
  /** Allowlisted Creatomate-supported Google Font family name (e.g. "Montserrat"). */
  font_family?: string;
  /** Text box width as percent of canvas (10–90). */
  width?: number;
  /** Background fill behind the text. Always sent: '' means "no background" (the editor never
   *  shows a design-default background, so the render must not invent one). */
  background_color?: string;
}

interface AssemblyOptions {
  captions_enabled?: boolean;
  captions_text?: string;
  captions_srt_url?: string;
  caption_design?: string;
  caption_spacing?: string;
  avatar_zoom?: number;
  /** Vertical avatar offset as a fraction of frame height; positive = down. Applied to the
   *  Creatomate Base-Video y. Mirrored by the editor preview transform for WYSIWYG. */
  avatar_offset_y?: number;
  text_overlays?: AssemblyTextOverlay[];
  /** BYO base video flow (e.g. local test upload): send so backend can synthesise a status record. */
  avatar_video_url?: string;
  video_format?: string;
}

interface UseUGCVideoAssemblyReturn {
  assembleVideo: (
    videoId: string,
    timelineSegments: MediaSegment[],
    backgroundMusicUrl?: string,
    /** 0–100; sent to Creatomate as mix %. Default 30. */
    backgroundMusicVolumePercent?: number,
    options?: AssemblyOptions
  ) => Promise<void>;
  isAssembling: boolean;
  assemblyError: string | null;
  assemblySuccess: boolean;
  clearAssemblyError: () => void;
}

const ASSEMBLY_POLL_INTERVAL_MS = 15000;
const ASSEMBLY_POLL_MAX_ATTEMPTS = 40;

export const useUGCVideoAssembly = ({
  customerId,
  idToken,
  onFinalUrl,
}: UseUGCVideoAssemblyProps): UseUGCVideoAssemblyReturn => {
  const [isAssembling, setIsAssembling] = useState(false);
  const [assemblyError, setAssemblyError] = useState<string | null>(null);
  const [assemblySuccess, setAssemblySuccess] = useState(false);
  const assemblyPollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const assemblyPollAttemptsRef = useRef(0);

  const clearAssemblyError = useCallback(() => {
    setAssemblyError(null);
    setAssemblySuccess(false);
  }, []);

  const assembleVideo = useCallback(
    async (
      videoId: string,
      timelineSegments: MediaSegment[],
      backgroundMusicUrl = '',
      backgroundMusicVolumePercent = 30,
      options?: AssemblyOptions
    ) => {
      if (!customerId || !idToken) {
        setAssemblyError('Missing customer or authentication.');
        return;
      }

      setIsAssembling(true);
      setAssemblyError(null);
      setAssemblySuccess(false);
      assemblyPollAttemptsRef.current = 0;

      try {
        const vol = Math.min(
          100,
          Math.max(0, Math.round(Number(backgroundMusicVolumePercent) || 0))
        );
        const body = {
          background_media: timelineSegments.map((s) => ({
            media_id: s.media_id,
            time: s.time,
            duration: s.duration,
            ...(s.fit !== undefined ? { fit: s.fit } : {}),
            ...(s.zoom !== undefined ? { zoom: s.zoom } : {}),
            ...(s.fade_in ? { fade_in: true } : {}),
            ...(s.fade_out ? { fade_out: true } : {}),
            ...(s.animation_in !== undefined ? { animation_in: s.animation_in } : {}),
            ...(s.anim_in_duration !== undefined ? { anim_in_duration: s.anim_in_duration } : {}),
            ...(s.animation_out !== undefined ? { animation_out: s.animation_out } : {}),
            ...(s.anim_out_duration !== undefined ? { anim_out_duration: s.anim_out_duration } : {}),
            ...(s.motion !== undefined ? { motion: s.motion } : {}),
            ...(s.volume !== undefined && s.volume > 0 ? { volume: s.volume } : {}),
            ...(s.opacity !== undefined && s.opacity !== 100 ? { opacity: s.opacity } : {}),
            ...(s.trim_start !== undefined && s.trim_start > 0 ? { trim_start: s.trim_start } : {}),
            ...(s.trim_end !== undefined && s.trim_end > 0 ? { trim_end: s.trim_end } : {}),
            ...(s.x_pct !== undefined ? { x_pct: s.x_pct } : {}),
            ...(s.y_pct !== undefined ? { y_pct: s.y_pct } : {}),
            ...(s.width_pct !== undefined ? { width_pct: s.width_pct } : {}),
            ...(s.height_pct !== undefined ? { height_pct: s.height_pct } : {}),
          })),
          background_music_url: backgroundMusicUrl.trim(),
          background_music_volume: vol,
          captions_enabled: options?.captions_enabled === true,
          captions_text: options?.captions_text?.trim() ?? '',
          captions_srt_url: options?.captions_srt_url?.trim() ?? '',
          caption_design: options?.caption_design?.trim() ?? '',
          caption_spacing: options?.caption_spacing?.trim() ?? '',
          ...(options?.avatar_zoom !== undefined ? { avatar_zoom: options.avatar_zoom } : {}),
          ...(options?.avatar_offset_y !== undefined ? { avatar_offset_y: options.avatar_offset_y } : {}),
          ...(options?.text_overlays?.length ? { text_overlays: options.text_overlays } : {}),
          ...(options?.avatar_video_url ? { avatar_video_url: options.avatar_video_url, customer_id: customerId } : {}),
          ...(options?.video_format ? { video_format: options.video_format } : {}),
        };
        await postUGCVideoAssembly(videoId, body, idToken);

        await new Promise<void>((resolve, reject) => {
          const pollStatus = async (): Promise<void> => {
            try {
              const statusResponse = await pollUGCVideoStatus(videoId, idToken);
              // Once Creatomate finishes, the webhook sets cloudfront_url to the FINAL composited
              // render. Never fall back to avatar_video_url here — that would resolve "success"
              // with the unrendered base video, so any media/text overlays the user added would
              // be silently dropped from the URL the parent stores and later saves to platforms.
              const videoUrl = statusResponse.cloudfront_url;

              if (statusResponse.status === 'COMPLETED' && videoUrl) {
                if (assemblyPollTimeoutRef.current) {
                  clearTimeout(assemblyPollTimeoutRef.current);
                  assemblyPollTimeoutRef.current = null;
                }
                onFinalUrl?.(videoUrl);
                setAssemblySuccess(true);
                setIsAssembling(false);
                assemblyPollAttemptsRef.current = 0;
                resolve();
                return;
              }

              if (statusResponse.status === 'FAILED') {
                if (assemblyPollTimeoutRef.current) {
                  clearTimeout(assemblyPollTimeoutRef.current);
                  assemblyPollTimeoutRef.current = null;
                }
                const msg = normalizeUGCVideoError(statusResponse.error ?? 'Assembly failed.');
                setAssemblyError(msg);
                setIsAssembling(false);
                reject(new Error(msg));
                return;
              }

              if (assemblyPollAttemptsRef.current < ASSEMBLY_POLL_MAX_ATTEMPTS) {
                assemblyPollAttemptsRef.current++;
                assemblyPollTimeoutRef.current = setTimeout(pollStatus, ASSEMBLY_POLL_INTERVAL_MS);
              } else {
                if (assemblyPollTimeoutRef.current) {
                  clearTimeout(assemblyPollTimeoutRef.current);
                  assemblyPollTimeoutRef.current = null;
                }
                const msg = 'Assembly is taking longer than expected. Please check back later.';
                setAssemblyError(msg);
                setIsAssembling(false);
                reject(new Error(msg));
              }
            } catch (err) {
              if (assemblyPollAttemptsRef.current < ASSEMBLY_POLL_MAX_ATTEMPTS) {
                assemblyPollAttemptsRef.current++;
                assemblyPollTimeoutRef.current = setTimeout(pollStatus, ASSEMBLY_POLL_INTERVAL_MS);
              } else {
                if (assemblyPollTimeoutRef.current) {
                  clearTimeout(assemblyPollTimeoutRef.current);
                  assemblyPollTimeoutRef.current = null;
                }
                const msg = err instanceof Error ? err.message : 'Failed to get final video.';
                setAssemblyError(msg);
                setIsAssembling(false);
                reject(err);
              }
            }
          };

          assemblyPollTimeoutRef.current = setTimeout(pollStatus, ASSEMBLY_POLL_INTERVAL_MS);
        });
      } catch (err) {
        debug.error('Assembly failed:', err);
        setAssemblyError(normalizeUGCVideoError(err));
        setIsAssembling(false);
        throw err;
      }
    },
    [customerId, idToken, onFinalUrl]
  );

  useEffect(() => {
    return () => {
      if (assemblyPollTimeoutRef.current) {
        clearTimeout(assemblyPollTimeoutRef.current);
      }
    };
  }, []);

  return {
    assembleVideo,
    isAssembling,
    assemblyError,
    assemblySuccess,
    clearAssemblyError,
  };
};

export default useUGCVideoAssembly;
